'use client';
import React, { useState, useCallback } from 'react';
import { Search, Newspaper, Clock, Eye, ArrowRight, Bookmark } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface NewsArticle {
  id: string;
  title: string;
  source: string;
  category: string;
  location: string;
  published: string;
  views: number;
  image_url: string;
  excerpt: string;
  is_featured: boolean;
  read_time: string;
}

const ARTICLES: NewsArticle[] = [
{ id: '1', title: 'Kenya GDP Grows 5.8% in Q2 2026 — Strongest Quarter in Three Years', source: 'Business Daily', category: 'Business', location: 'Nairobi', published: '2 hours ago', views: 12400, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_186c09f92-1764894959449.png", excerpt: 'Kenya\'s economy expanded at its fastest pace in three years, driven by strong performance in agriculture and technology sectors.', is_featured: true, read_time: '4 min' },
{ id: '2', title: 'Safaricom Launches 5G Network in Nairobi, Mombasa and Kisumu', source: 'TechWeez', category: 'Technology', location: 'Nairobi', published: '5 hours ago', views: 8900, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1d90800f1-1764748931288.png", excerpt: 'Safaricom has officially launched its 5G network covering major urban centres, promising speeds up to 1Gbps for consumers.', is_featured: true, read_time: '3 min' },
{ id: '3', title: 'Gor Mahia Wins KPL Title for Record 20th Time', source: 'Nation Sports', category: 'Sports', location: 'Nairobi', published: '1 day ago', views: 34500, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12b5da68b-1784558011322.png", excerpt: 'Gor Mahia clinched the Kenya Premier League title with a 3-0 victory over AFC Leopards at Nyayo Stadium.', is_featured: false, read_time: '2 min' },
{ id: '4', title: 'Nairobi Expressway Extends to Jomo Kenyatta Airport', source: 'Standard Media', category: 'Infrastructure', location: 'Nairobi', published: '2 days ago', views: 21000, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_136b2ac88-1784528041185.png", excerpt: 'The government has announced the extension of the Nairobi Expressway to JKIA, cutting travel time to under 20 minutes.', is_featured: false, read_time: '5 min' },
{ id: '5', title: 'Kenya Wins 3 Gold Medals at World Athletics Championships', source: 'Athletics Kenya', category: 'Sports', location: 'Nairobi', published: '3 days ago', views: 56000, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f895fc7d-1784558009382.png", excerpt: 'Kenyan athletes dominated the middle and long-distance events, cementing the country\'s status as a global athletics powerhouse.', is_featured: false, read_time: '3 min' },
{ id: '6', title: 'New Mombasa Port Expansion to Handle 2M Containers Annually', source: 'Coast Business', category: 'Business', location: 'Mombasa', published: '4 days ago', views: 9800, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1374f2fc8-1784558006815.png", excerpt: 'The Kenya Ports Authority has completed Phase 2 of the Mombasa Port expansion, doubling container handling capacity.', is_featured: false, read_time: '4 min' }];


const CATEGORIES = ['All', 'Business', 'Technology', 'Sports', 'Infrastructure', 'Politics', 'Health', 'Entertainment'];
const LOCATIONS = ['All Regions', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'National'];
const SORT_OPTIONS = ['Latest', 'Most Read', 'Trending'];

export default function NewsPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [location, setLocation] = useState('All Regions');
  const [sort, setSort] = useState('Latest');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const filtered = ARTICLES.filter((a) => {
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.source.toLowerCase().includes(search.toLowerCase()) || a.excerpt.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || a.category === category;
    const matchLoc = location === 'All Regions' || a.location === location;
    return matchSearch && matchCat && matchLoc;
  }).sort((a, b) => {
    if (sort === 'Most Read') return b.views - a.views;
    return 0;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #00BCD4 0%, transparent 50%), radial-gradient(circle at 70% 30%, #7C4DFF 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
            style={{ background: 'rgba(0,188,212,0.15)', color: '#00BCD4', border: '1px solid rgba(0,188,212,0.3)' }}>
              <Newspaper size={12} /> Kenya & East Africa News
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Stay <span style={{ color: '#00BCD4' }}>Informed</span> with Latest News
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Business, technology, sports, and more. Curated from top Kenyan and East African sources.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search news, topics, or sources…"
              className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((c) =>
            <button key={c} onClick={() => setCategory(c)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: category === c ? 'rgba(0,188,212,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#00BCD4' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(0,188,212,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            )}
          </div>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pt-2">
            {LOCATIONS.map((l) =>
            <button key={l} onClick={() => setLocation(l)}
            className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: location === l ? 'rgba(124,77,255,0.15)' : 'rgba(255,255,255,0.04)', color: location === l ? '#7C4DFF' : 'rgba(255,255,255,0.45)', border: `1px solid ${location === l ? 'rgba(124,77,255,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                {l}
              </button>
            )}
            <div className="flex-shrink-0 w-px h-4 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {SORT_OPTIONS.map((s) =>
            <button key={s} onClick={() => setSort(s)}
            className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: sort === s ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: sort === s ? '#00C853' : 'rgba(255,255,255,0.45)', border: `1px solid ${sort === s ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                {s}
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#00BCD4', fontWeight: 700 }}>{filtered.length}</span> articles found
            </span>
            <Link href="/login"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
            style={{ background: '#00BCD4', color: '#000' }}>
              Publish News <ArrowRight size={12} />
            </Link>
          </div>

          {/* Featured article */}
          {filtered.filter((a) => a.is_featured).slice(0, 1).map((article) =>
          <div key={article.id} className="rounded-2xl overflow-hidden mb-4"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,188,212,0.2)' }}>
              <div className="relative h-52">
                <img src={article.image_url} alt={article.title}
              className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 50%)' }} />
                <span className="absolute top-3 left-3 text-[10px] px-2 py-1 rounded-lg font-700"
              style={{ background: '#00BCD4', color: '#000' }}>Featured</span>
                <button onClick={() => toggleSave(article.id)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: savedIds.has(article.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                  <Bookmark size={13} fill={savedIds.has(article.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                </button>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="text-[10px] px-2 py-0.5 rounded-md font-600 mb-2 inline-block"
                style={{ background: 'rgba(0,188,212,0.25)', color: '#00BCD4' }}>{article.category}</span>
                  <h2 className="text-base font-800 text-white leading-tight">{article.title}</h2>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{article.source}</span>
                    <div className="flex items-center gap-1">
                      <Clock size={10} style={{ color: 'rgba(255,255,255,0.4)' }} />
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{article.published}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye size={10} style={{ color: 'rgba(255,255,255,0.4)' }} />
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{article.views.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Article list */}
          <div className="flex flex-col gap-3">
            {filtered.filter((a) => !a.is_featured || filtered.indexOf(a) > 0).map((article) =>
            <div key={article.id} className="flex gap-3 p-3 rounded-2xl"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <img src={article.image_url} alt={article.title}
              className="w-20 h-20 rounded-xl object-cover flex-shrink-0" loading="lazy" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md font-600 mb-1 inline-block"
                    style={{ background: 'rgba(0,188,212,0.12)', color: '#00BCD4' }}>{article.category}</span>
                      <p className="text-sm font-700 text-white line-clamp-2 leading-tight">{article.title}</p>
                    </div>
                    <button onClick={() => toggleSave(article.id)} className="flex-shrink-0 p-1">
                      <Bookmark size={13} fill={savedIds.has(article.id) ? '#00BCD4' : 'none'} style={{ color: savedIds.has(article.id) ? '#00BCD4' : 'rgba(255,255,255,0.3)' }} />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="text-xs font-600" style={{ color: 'rgba(255,255,255,0.55)' }}>{article.source}</span>
                    <div className="flex items-center gap-1">
                      <Clock size={9} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{article.published}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye size={9} style={{ color: 'rgba(255,255,255,0.35)' }} />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{article.views.toLocaleString()}</span>
                    </div>
                    <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{article.read_time} read</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(0,188,212,0.1), rgba(124,77,255,0.1))', border: '1px solid rgba(0,188,212,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">Publish Your News</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Reach thousands of readers across Kenya. Sign up to publish articles and press releases.</p>
            <Link href="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
            style={{ background: '#00BCD4', color: '#000' }}>
              <Newspaper size={16} /> Start Publishing
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>);

}